import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CompaniesMakersData } from '../../store/apiSlices/companies/companiesMakers/companiesMakersApiParser';
import ModalFooter from '../common/ModalFooter';
import Dropdown from '../common/Dropdown';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';

type MakerFormValues = {
  shortName: string;
  companyId: string;
  status: string;
};

interface EditCompaniesMakersModalProps {
  data: CompaniesMakersData;
  onClose: () => void;
  onSubmit: () => void;
  control: Control<MakerFormValues>;
  errors?: FieldErrors<MakerFormValues>;
  isEditMode?: boolean;
  isSubmitting?: boolean;
}

const EditCompaniesMakersModal: React.FC<EditCompaniesMakersModalProps> = ({
  data,
  onClose,
  onSubmit,
  control,
  errors,
  isEditMode = false,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={true}
      onClose={onClose}
      size="sm"
      title={isEditMode ? t('companies.editMaker') : t('companies.addMaker')}
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={onSubmit}
          isLoading={isSubmitting}
          submitText={
            isSubmitting
              ? isEditMode
                ? t('buttons.updating')
                : t('buttons.adding')
              : isEditMode
                ? t('buttons.update')
                : t('buttons.add')
          }
        />
      }
    >
      <FormField label={t('form.shortName')} sx={{ mt: 2 }} required>
        <Controller
          name="shortName"
          control={control}
          rules={{ required: t('form.validation.shortNameRequired') }}
          render={({ field }) => (
            <InputField
              {...field}
              value={field.value || ''}
              placeholder={t('form.enterShortName')}
              error={!!errors?.shortName}
              helperText={errors?.shortName?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.company')} sx={{ mt: 2 }} required>
        <Controller
          name="companyId"
          control={control}
          rules={{ required: t('form.validation.companyRequired') }}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? ''}
              onChange={(e: any) => field.onChange(e.target.value)}
              options={data.companies}
              placeholder={t('form.selectCompany')}
              disabled={isEditMode}
              debounceMs={200}
              maxDisplayedOptions={50}
              error={!!errors?.companyId}
              helperText={errors?.companyId?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('common.status')} sx={{ mt: 2 }}>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? 'Active'}
              onChange={(e: any) => field.onChange(e.target.value)}
              options={data.statusOptions}
              placeholder={t('common.selectStatus')}
              hasSearch={false}
            />
          )}
        />
      </FormField>
    </Modal>
  );
};

export default EditCompaniesMakersModal;
